/**
 * Tipi TypeScript per Document Training
 * Corrispondono agli schemi Pydantic del backend
 */

/**
 * Training Status enum
 */
export enum TrainingStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  ANNOTATED = 'annotated',
  VALIDATED = 'validated',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

/**
 * Document Lab Training Response
 */
export interface DocumentLabTrainingResponse {
  training_doc_id: string;
  company_id: string;
  file_path: string;
  dataset_name: string;
  dataset_version: string;
  document_type: string; // Nota: sarà document_type_id dopo migrazione
  document_type_id?: string | null;
  document_schema_id?: string | null;
  training_status: TrainingStatus;
  notes: string | null;
  annotation_count: number;
  validation_quality_score: number | null;
  has_human_annotations: boolean;
  model_versions_used: string[] | null;
  source_url: string | null;
  attribute_metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  // Extraction metadata
  last_extraction_id?: string | null;
  last_extraction_at?: string | null;
  last_extraction_strategy?: string | null;
  extraction_confidence?: number | null;
  extraction_count?: number;

  // Schema info
  detected_schema_id?: string | null;
  detected_schema_name?: string | null;
  is_dynamic_schema?: boolean;

  // Ground truth
  ground_truth_path?: string | null;

  // Warnings (e.g. duplicate filename)
  warnings?: string[] | null;

  // Split/Segment info
  parent_training_document_id?: string | null;
  is_split_segment?: boolean;
  page_range_start?: number | null;
  page_range_end?: number | null;
  original_page_count?: number | null;
  split_count?: number;  // Number of child documents (if this is a parent)
  classification_metadata?: Record<string, unknown> | null;
}

/**
 * Document Lab Training Create Request
 */
export interface DocumentLabTrainingCreate {
  file_path: string;
  dataset_name: string;
  dataset_version: string;
  document_type?: string; // Optional: document type key
  document_type_id?: string; // Optional: document type UUID - backend requires either this or document_type
  notes?: string | null;
  source_url?: string | null;
  attribute_metadata?: Record<string, unknown> | null;
}

/**
 * Document Lab Training Update Request
 */
export interface DocumentLabTrainingUpdate {
  dataset_name?: string | null;
  dataset_version?: string | null;
  training_status?: TrainingStatus | null;
  notes?: string | null;
  validation_quality_score?: number | null;
  model_versions_used?: string[] | null;
  attribute_metadata?: Record<string, unknown> | null;
  document_type?: string | null;
  document_type_id?: string | null;
  document_schema_id?: string | null;
}

/**
 * Document Lab Training List Filters
 */
export interface DocumentLabTrainingListFilters {
  skip?: number;
  limit?: number;
  dataset_name?: string;
  document_type?: string;
  document_type_id?: string;
  training_status?: TrainingStatus;
  has_annotations?: boolean;
}

/**
 * Document Lab Training Human Parsed Response
 */
export interface DocumentLabTrainingHumanParsedResponse {
  annotation_id: string;
  training_doc_id: string;
  annotation_version: number;
  parsed_data: Record<string, unknown>;
  page_annotations: Record<string, unknown>;
  confidence_scores: Record<string, unknown> | null;
  annotated_by_user_id: string;
  annotation_time_seconds: number | null;
  difficulty_rating: number | null;
  notes: string | null;
  is_verified: boolean;
  verified_by_user_id: string | null;
  verified_at: string | null;
  verification_notes: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Ground Truth types
 */
export interface GroundTruthRequest {
  structured_data: Record<string, unknown>;
  notes?: string | null;
}

export interface GroundTruthResponse {
  document_id: string;
  document_type: string;
  schema_code: string;
  source_file: string;
  notes: string;
  metadata: Record<string, unknown>;
  structured_data: Record<string, unknown>;
  draft?: Record<string, unknown> | null;
  draft_updated_at?: string | null;
  is_draft_only?: boolean;
}

export interface GroundTruthDraftRequest {
  structured_data: Record<string, unknown>;
  notes?: string | null;
  other_fields?: Record<string, unknown> | null;
}

/**
 * Document Lab Training Human Parsed Create Request
 */
export interface DocumentLabTrainingHumanParsedCreate {
  annotation_version?: number;
  parsed_data: Record<string, unknown>;
  page_annotations: Record<string, unknown>;
  confidence_scores?: Record<string, unknown> | null;
  annotation_time_seconds?: number | null;
  difficulty_rating?: number | null;
  notes?: string | null;
  is_verified?: boolean;
  verification_notes?: string | null;
}

/**
 * Document Lab Training Human Parsed Update Request
 */
export interface DocumentLabTrainingHumanParsedUpdate {
  parsed_data?: Record<string, unknown> | null;
  page_annotations?: Record<string, unknown> | null;
  confidence_scores?: Record<string, unknown> | null;
  annotation_time_seconds?: number | null;
  difficulty_rating?: number | null;
  notes?: string | null;
  is_verified?: boolean | null;
  verification_notes?: string | null;
}

/**
 * Training Annotation List Filters
 */
export interface TrainingAnnotationListFilters {
  skip?: number;
  limit?: number;
  is_verified?: boolean;
}

/**
 * Extraction Strategy Response (API 3)
 */
export interface ExtractionStrategyResponse {
  name: string;
  description: string;
  extraction_source: string;
  model_version: string;
  supported_file_types: string[];
}

/**
 * Extraction Result Response (API 7, 8)
 */
export interface ExtractionResultResponse {
  parsing_id: string;
  training_doc_id: string;
  strategy: string;
  model_version: string;
  parse_content: Record<string, unknown>;
  ocr_output: string | null;
  confidence: number;
  field_confidence_scores: Record<string, number>;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  duration_ms: number;
  tokens_used: number;
  cost_usd: number;
  is_selected?: boolean;
  created_at: string;
}

/**
 * Select Extraction Response (API 9)
 */
export interface SelectExtractionResponse {
  parsing_id: string;
  is_selected: boolean;
  message: string;
}

/**
 * Job Type enum
 */
export type JobType = 'parsing' | 'extraction' | 'classification' | 'ocr';

/**
 * Job Status enum
 */
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * Job History Item - for tracking parsing and classification jobs
 */
export interface JobHistoryItem {
  job_id: string;
  job_type: JobType;
  strategy?: string | null;
  model_version?: string | null;
  status: JobStatus;
  confidence?: number | null;
  cost_usd?: number | null;
  duration_ms?: number | null;
  tokens_used?: number | null;
  error_message?: string | null;
  created_at: string;
  completed_at?: string | null;
  // For extraction/parsing jobs
  parsing_id?: string | null;
  is_selected?: boolean;
  // For classification jobs
  document_type?: string | null;
  is_multi_document?: boolean;  // Whether multiple document types were detected
  document_types_detected?: DetectedDocumentType[] | null;  // All detected types with page ranges
  // OCR output
  ocr_output?: string | null;
  // For batch/OCR jobs
  batch_job_id?: string | null;
  runpod_job_id?: string | null;
  ocr_output_preview?: string | null;
}

/**
 * Job History Response - wrapper from API
 */
export interface JobHistoryResponse {
  training_document_id: string;
  jobs: JobHistoryItem[];
  summary: {
    total_jobs: number;
    completed: number;
    failed: number;
    pending: number;
    in_progress: number;
    total_cost_usd: number;
    extraction_jobs: number;
    classification_jobs: number;
    ocr_jobs: number;
  };
}

/**
 * Validation status enum for fields
 */
export type FieldValidationStatus = 'pending' | 'confirmed' | 'corrected' | 'rejected';

/**
 * Field Value Response - for extraction field values
 * Matches actual API response format
 */
export interface FieldValueResponse {
  field_value_id: string;
  attribute_key: string;
  attribute_label?: string | null;
  data_type?: string | null;
  category?: string | null;
  extracted_value: unknown;
  validated_value?: unknown | null;
  effective_value: unknown;
  validation_status: FieldValidationStatus;
  confidence_score: number | null;
  validated_by_user_id?: string | null;
  validated_by_username?: string | null;
  validated_at?: string | null;
  validation_notes?: string | null;
  bounding_box?: unknown | null;
  page_number?: number | null;
  has_corrections: boolean;
  corrections_count: number;
}

/**
 * Field Value Update Request
 */
export interface FieldValueUpdate {
  value: string;
  reason?: string | null;
}

/**
 * Field Validation Request
 * Backend expects 'status' field with validation status
 */
export interface FieldValidationRequest {
  status: 'confirmed' | 'rejected';
  notes?: string | null;
}

/**
 * Field Validation Response
 */
export interface FieldValidationResponse {
  field_value_id: string;
  is_validated: boolean;
  is_correct: boolean;
  validated_by_user_id: string;
  validated_at: string;
  message: string;
}

/**
 * Field History Item - for tracking changes to a field
 */
export interface FieldHistoryItem {
  history_id: string;
  field_value_id: string;
  previous_value: unknown;
  new_value: unknown;
  changed_by_user_id: string;
  change_type: 'edit' | 'validation' | 'reset';
  notes?: string | null;
  created_at: string;
}

/**
 * Batch Parsing Request
 */
export interface BatchParsingRequest {
  training_document_ids: string[];
  strategy: string;
  schema_id?: string | null;
}

/**
 * Batch Parsing Response
 */
export interface BatchParsingResponse {
  batch_job_id: string;
  status: string;
  total_documents: number;
}

/**
 * Batch Classification Request
 */
export interface BatchClassificationRequest {
  training_document_ids: string[];
}

/**
 * Batch Classification Response
 */
export interface BatchClassificationResponse {
  batch_job_id: string;
  status: string;
  total_documents: number;
}

/**
 * Batch Reassign Type Request
 */
export interface BatchReassignTypeRequest {
  training_document_ids: string[];
  document_type_id: string;
  document_schema_id?: string;
}

/**
 * Batch Reassign Type Response
 */
export interface BatchReassignTypeResponse {
  total: number;
  success: number;
  failed: number;
  document_type: string;
}

/**
 * Batch Delete Request
 */
export interface BatchDeleteRequest {
  training_document_ids: string[];
}

/**
 * Batch Delete Response
 */
export interface BatchDeleteResponse {
  deleted_count: number;
}

/**
 * Detected document type within a multi-document file
 */
export interface DetectedDocumentType {
  type: string;
  confidence: number;
  page_start?: number | null;
  page_end?: number | null;
}

/**
 * Classification Result Response
 */
export interface ClassificationResultResponse {
  training_document_id: string;
  document_type: string;
  document_type_id?: string | null;
  confidence: number;
  is_multi_document: boolean;
  document_types_detected: DetectedDocumentType[];
  origin_country?: string | null;
  destination_country?: string | null;
  origin_port?: string | null;
  destination_port?: string | null;
}

/**
 * Child Training Document Response (for split documents)
 */
export interface ChildTrainingDocumentResponse {
  training_document_id: string;
  document_type?: string | null;
  document_type_id?: string | null;
  page_start: number;
  page_end: number;
  page_count: number;
  confidence?: number | null;
  file_name?: string | null;
  training_status: TrainingStatus;
}

/**
 * Split Training Document Request
 */
export interface SplitTrainingDocumentRequest {
  page_ranges: Array<{
    start: number;
    end: number;
    document_type?: string;
    document_type_id?: string;
    confidence?: number;
  }>;
}

/**
 * Split Training Document Response
 */
export interface SplitTrainingDocumentResponse {
  parent_document_id: string;
  split_count: number;
  split_documents: ChildTrainingDocumentResponse[];
}

/**
 * Grouped Field Child - atomic field within a collapsible group
 */
export interface GroupedFieldChild {
  field_value_id: string;
  field_key: string;
  ai_field_path?: string | null;
  field_path?: string | null;
  extracted_value?: string | null;
  validated_value?: string | null;
  effective_value?: string | null;
  match_status?: string | null;
  match_confidence?: number | null;
  attribute_id?: string | null;
  attribute_key?: string | null;
  attribute_label?: string | null;
  validation_status: FieldValidationStatus;
  confidence_score?: number | null;
  array_index?: number | null;
}

/**
 * Field Group - collapsible group of related fields
 */
export interface FieldGroup {
  group_key: string;
  display_label: string;
  is_container: boolean;
  is_array_item: boolean;
  array_index?: number | null;
  nesting_level: number;
  collapsed_preview?: Record<string, unknown> | null;
  collapsed_preview_json?: string | null;
  children: GroupedFieldChild[];
  child_count: number;
  total_fields: number;
  validated_count: number;
  pending_count: number;
  exact_match_count: number;
  alias_match_count: number;
  unmatched_count: number;
}

/**
 * Grouped Fields Response - hierarchical view of extraction fields
 */
export interface GroupedFieldsResponse {
  parsing_id: string;
  training_doc_id: string;
  groups: FieldGroup[];
  total_groups: number;
  total_fields: number;
  validated_fields: number;
  pending_fields: number;
  exact_matches: number;
  alias_matches: number;
  unmatched: number;
}

